import { Link2, Loader2, MoreHorizontal, Pin, Trash2, User, UserPlus } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { NotePanel } from "@/components/notes/note-panel"
import { PEEK_COMPACT_MENU_MIN_WIDTH, PeekAnchoredMenu, PeekMenuItem } from "@/components/platform/company-peek-parts"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { Skeleton } from "@/components/ui/primitives"
import { ToolbarSearchField } from "@/components/ui/toolbar-search-field"
import { WorkspaceCreateAction } from "@/components/workspace/workspace-create-action"
import { useTranslation } from "@/hooks/use-translation"
import { copyTextWithToast } from "@/lib/clipboard"
import { confirm } from "@/lib/confirm"
import { noteLinkPath } from "@/lib/note-deep-link"
import {
  deleteNote,
  fetchNotes,
  notifyNotesChanged,
  onNotesChanged,
  pinNote,
  unpinNote,
  type NoteListCursor,
  type NoteListResponse,
  type NoteListRow,
} from "@/lib/notes-api"
import { formatListAge } from "@/lib/relative-time"
import { cn } from "@/lib/utils"

const ROW_ICON_BUTTON =
  "flex size-5 items-center justify-center rounded-md text-ui-text-muted hover:bg-ui-surface-sunken hover:text-ui-text-primary"
const SEARCH_DEBOUNCE_MS = 250
const LOAD_MORE_ROOT_MARGIN = "240px 0px"
const NOTE_SKELETON_ROWS = [
  { title: "72%", snippet: "92%", age: 28 },
  { title: "46%", snippet: "64%", age: 36 },
  { title: "58%", snippet: "38%", age: 22 },
] as const

function scrollParent(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node; node = node.parentElement) {
    const { overflowY } = window.getComputedStyle(node)
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") return node
  }
  return null
}

function sameCursor(left: NoteListCursor | null, right: NoteListCursor | null) {
  return left != null && right != null && left.kind === right.kind && left.at === right.at && left.id === right.id
}

export function NotesDrawer({
  noteId = null,
  onNoteLinkConsumed,
}: {
  noteId?: number | null
  onNoteLinkConsumed?: () => void
}) {
  const [open, setOpen] = useState<{ id: number | null; shares: boolean } | null>(null)
  const close = useCallback(() => setOpen(null), [])

  useEffect(() => {
    if (noteId == null) return
    setOpen({ id: noteId, shares: false })
    onNoteLinkConsumed?.()
  }, [noteId, onNoteLinkConsumed])

  if (open != null) {
    return (
      <div className="absolute inset-0 flex min-h-0 flex-col">
        <NotePanel
          noteId={open.id}
          openShares={open.shares}
          onBack={close}
          onCreated={(id) => setOpen((current) => (current ? { ...current, id } : current))}
        />
      </div>
    )
  }
  return (
    <NoteList
      onOpen={(id, shares = false) => setOpen({ id, shares })}
      onCreate={() => setOpen({ id: null, shares: false })}
    />
  )
}

function NoteList({ onOpen, onCreate }: { onOpen: (id: number, shares?: boolean) => void; onCreate: () => void }) {
  const { t } = useTranslation("notes")
  const [query, setQuery] = useState("")
  const [notes, setNotes] = useState<NoteListRow[]>([])
  const [status, setStatus] = useState<NoteListResponse["status"] | null>(null)
  const [next, setNext] = useState<NoteListCursor | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const generation = useRef(0)
  const loadingMoreRef = useRef(false)
  const queryRef = useRef(query)
  const nextRef = useRef(next)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  queryRef.current = query
  nextRef.current = next

  const loadFirst = useCallback(
    (gen: number) => {
      if (gen !== generation.current) return
      loadingMoreRef.current = false
      setLoadingMore(false)
      setNext(null)
      setLoading(true)
      fetchNotes(queryRef.current)
        .then((page) => {
          if (gen !== generation.current) return
          setNotes(page.notes)
          setStatus(page.status)
          setNext(page.next_cursor)
        })
        .catch((error: unknown) => {
          if (gen !== generation.current) return
          toast.error(error instanceof Error ? error.message : t("loadFailed"))
        })
        .finally(() => {
          if (gen === generation.current) setLoading(false)
        })
    },
    [t],
  )

  useEffect(() => {
    const gen = ++generation.current
    setNext(null)
    const timer = window.setTimeout(() => loadFirst(gen), SEARCH_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [query, loadFirst])

  useEffect(() => onNotesChanged(() => loadFirst(++generation.current)), [loadFirst])

  useEffect(() => {
    return () => {
      generation.current += 1
    }
  }, [])

  useEffect(() => {
    const root = listRef.current ? scrollParent(listRef.current) : null
    if (root) root.scrollTop = 0
  }, [query])

  const loadMore = useCallback(() => {
    const cursor = nextRef.current
    if (cursor == null || loadingMoreRef.current) return
    const gen = generation.current
    loadingMoreRef.current = true
    setLoadingMore(true)
    fetchNotes(queryRef.current, cursor)
      .then((page) => {
        if (gen !== generation.current) return
        setNotes((prev) => {
          const seen = new Set(prev.map((row) => row.id))
          return [...prev, ...page.notes.filter((row) => !seen.has(row.id))]
        })
        const incoming = page.notes.length === 0 ? null : page.next_cursor
        setNext(sameCursor(cursor, incoming) ? null : incoming)
      })
      .catch((error: unknown) => {
        if (gen !== generation.current) return
        toast.error(error instanceof Error ? error.message : t("loadFailed"))
      })
      .finally(() => {
        if (gen !== generation.current) return
        loadingMoreRef.current = false
        setLoadingMore(false)
      })
  }, [t])

  const loadMoreRef = useRef(loadMore)
  loadMoreRef.current = loadMore

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || next == null || loading) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMoreRef.current()
      },
      { root: scrollParent(sentinel), rootMargin: LOAD_MORE_ROOT_MARGIN },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [next, loading, notes.length])

  return (
    <div ref={listRef} className="space-y-3">
      <div className="flex items-center gap-1.5">
        <ToolbarSearchField
          value={query}
          onChange={setQuery}
          placeholder={t("searchPlaceholder")}
          title={t("searchPlaceholder")}
          clearLabel={t("searchClear")}
          slashHotkey={false}
          loading={loading}
          className="h-7 min-w-0 flex-1 shrink"
        />
        <WorkspaceCreateAction label={t("new")} onClick={onCreate} />
      </div>
      {status === "too_short" ? <p className="text-xs text-ui-text-muted">{t("searchTooShort")}</p> : null}
      {!loading && status != null && status !== "too_short" && notes.length === 0 ? (
        <p className="text-xs text-ui-text-muted">{query.trim() ? t("nothingFound") : t("empty")}</p>
      ) : null}
      {loading && notes.length === 0 && status !== "too_short" ? (
        <NoteListSkeleton />
      ) : (
        <div className="flex flex-col gap-0.5">
          {notes.map((note) => (
            <NoteRow key={note.id} note={note} onOpen={onOpen} />
          ))}
          {next ? (
            <div
              ref={sentinelRef}
              className={cn("flex items-center justify-center text-ui-text-muted", loadingMore ? "h-6" : "h-px")}
            >
              {loadingMore ? (
                <>
                  <Loader2 aria-hidden className="size-3.5 animate-spin" />
                  <span className="sr-only">{t("loadingMore")}</span>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

function NoteListSkeleton() {
  const { t } = useTranslation("notes")

  return (
    <div className="flex flex-col gap-0.5" role="status" aria-label={t("loading")}>
      {NOTE_SKELETON_ROWS.map((row) => (
        <div key={row.title} className="flex items-start gap-2 rounded-lg px-2 py-1.5">
          <span className="flex h-5 w-4 shrink-0 items-center justify-center">
            <Skeleton className="size-1.5 rounded-full" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex h-5 items-center">
              <Skeleton className="h-3 rounded-sm" style={{ width: row.title }} />
            </div>
            <div className="flex h-4 items-center">
              <Skeleton className="h-2.5 rounded-sm" style={{ width: row.snippet }} />
            </div>
          </div>
          <div className="flex h-5 items-center">
            <Skeleton className="h-2.5 rounded-sm" style={{ width: row.age }} />
          </div>
        </div>
      ))}
    </div>
  )
}

function NoteRow({ note, onOpen }: { note: NoteListRow; onOpen: (id: number, shares?: boolean) => void }) {
  const { t, locale } = useTranslation("notes")
  const [menuOpen, setMenuOpen] = useState(false)
  const age = formatListAge(note.updated_at, locale)
  const pinLabel = note.pinned ? t("unpin") : t("pin")

  const copyLink = () => {
    setMenuOpen(false)
    void copyTextWithToast(`${window.location.origin}${noteLinkPath(note.id)}`, t("linkCopied"), t("copyFailed"))
  }

  const togglePin = async () => {
    setMenuOpen(false)
    try {
      if (note.pinned) await unpinNote(note.id)
      else await pinNote(note.id)
      notifyNotesChanged()
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : t("loadFailed"))
    }
  }

  const remove = async () => {
    setMenuOpen(false)
    if (!(await confirm({ message: t("deleteConfirm"), tone: "danger" }))) return
    try {
      await deleteNote(note.id)
      notifyNotesChanged()
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : t("loadFailed"))
    }
  }

  return (
    <div
      className={cn(
        "group relative flex items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-ui-surface-raised",
        menuOpen && "bg-ui-surface-raised",
      )}
    >
      <button
        type="button"
        className="absolute inset-0 rounded-lg"
        aria-label={note.shared ? `${t("open")}: ${note.title}. ${t("shared")}` : `${t("open")}: ${note.title}`}
        onClick={() => onOpen(note.id)}
      />
      <button
        type="button"
        className={cn(
          "relative z-10 flex h-5 w-4 shrink-0 items-center justify-center text-ui-text-muted hover:text-ui-text-primary",
          note.pinned ? "pointer-events-auto" : "pointer-events-none group-hover:pointer-events-auto",
        )}
        aria-label={pinLabel}
        onClick={(event) => {
          event.currentTarget.blur()
          void togglePin()
        }}
      >
        {note.pinned ? (
          <Pin className="size-3.5 fill-current" />
        ) : (
          <>
            <span className="size-1.5 rounded-full bg-current/40 group-hover:hidden" />
            <Pin className="hidden size-3.5 group-hover:block" />
          </>
        )}
      </button>
      <div className="pointer-events-none relative min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1">
          <div className="truncate text-[13px] leading-5 text-ui-text-primary">{note.title}</div>
          {note.shared ? (
            <ActionHintTooltip label={t("shared")} side="bottom" className="pointer-events-auto h-5 shrink-0">
              <span className="inline-flex text-ui-text-muted" aria-hidden onClick={() => onOpen(note.id)}>
                <User className="size-3.5" />
              </span>
            </ActionHintTooltip>
          ) : null}
        </div>
        {note.snippet ? <div className="truncate text-[11px] leading-4 text-ui-text-muted">{note.snippet}</div> : null}
      </div>
      <div className="pointer-events-none relative z-10 flex shrink-0 items-center gap-1.5">
        <div className={cn("pointer-events-auto hidden items-center gap-0.5 group-hover:flex", menuOpen && "flex")}>
          <PeekAnchoredMenu
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            menuMinWidth={PEEK_COMPACT_MENU_MIN_WIDTH}
            trigger={(ref) => (
              <button
                ref={ref}
                type="button"
                className={cn(ROW_ICON_BUTTON, menuOpen && "bg-ui-surface-sunken text-ui-text-primary")}
                aria-label={t("actions")}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((prev) => !prev)}
              >
                <MoreHorizontal className="size-3.5" />
              </button>
            )}
          >
            <PeekMenuItem icon={<Link2 />} label={t("copyLink")} onSelect={copyLink} />
            <PeekMenuItem icon={<Pin />} label={pinLabel} onSelect={() => void togglePin()} />
            {note.author ? (
              <>
                <PeekMenuItem
                  icon={<UserPlus />}
                  label={t("openAccess")}
                  onSelect={() => {
                    setMenuOpen(false)
                    onOpen(note.id, true)
                  }}
                />
                <div className="my-1 border-t border-ui-border" role="separator" />
                <PeekMenuItem icon={<Trash2 />} label={t("delete")} danger onSelect={() => void remove()} />
              </>
            ) : null}
          </PeekAnchoredMenu>
        </div>
        <span className="text-xs leading-5 text-ui-text-muted tabular-nums">{age}</span>
      </div>
    </div>
  )
}
